<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <TitleCard
        :is-show-mode="isShowMode"
        :is-process-mode="isProcessMode"
        :is-edit-mode="isEditMode"
        :disabled="invalid"
        :saveLoading="saveBtnLoading"
        :selected-item="publication"
        :is-no-changes="isNoChanges"
        @save="savePublication"
      />

      <v-row
        class="mb-0 mt-2"
        style="margin-right: -14px"
      >
        <v-col
          ref="workList"
          cols="12"
          class="pt-1 pb-2 pr-2 overflow-auto"
        >
          <WorkTitleCard :title="workTypeRusName(workTypeName.Publication)" />

          <AuthorsCard
            class="mt-4"
            title="Автор"
            :agreementFileList="agreementFileList"
            :selected-item.sync="publication.commonWork.authors"
            :is-show-mode="isShowMode"
            :is-foreign-publication="publication.isForeign"
            :upload-files="true"
            is-show-isforeignauthor
            @updateInvalid="updateAuthorsInvalid"
            @uploadAgreementFile="initUploadAgreementFile"
            @removeAgreementFile="initRemoveAgreementFile"
          >
            <CardRow>
              <v-checkbox
                class="rounded-lg mini-text field-edit d-inline-block"
                v-model="publication.isForeign"
                label="Статья на иностранном языке"
                hide-details
                dense
                :disabled="isShowMode"
              />
            </CardRow>
          </AuthorsCard>

          <PublicationCard
            class="mt-4"
            title="Основная информация"
            :selected-item.sync="publication"
            :is-show-mode="isShowMode"
            @updateInvalid="updatePublicationInvalid"
          />

          <PublicationPagesInTheEditionCard
            class="mt-4"
            :selected-item.sync="publication"
            :is-show-mode="isShowMode"
            @updateInvalid="updatePagesInTheEditionCardInvalid"
          />

          <CommentCard
            v-if="Boolean(publication.commonWork.comment)"
            class="mt-4"
            :comment="publication.commonWork.comment"
          />

          <CreatorCard :creator-name="publication.commonWork.creator.fullName" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { publicationApi } from '@/api'
import TitleCard from '@/components/Works/elements/TitleCard'
import AuthorsCard from '@/components/Works/elements/AuthorsCard'
import { Publication } from '@/models'
import PublicationCard from '@/components/Works/Publication/PublicationCard'
import PublicationPagesInTheEditionCard from '@/components/Works/Publication/PublicationPagesInTheEditionCard'
import CommentCard from '@/components/Works/elements/CommentCard.vue'
import { mapActions, mapGetters } from 'vuex'
import { workListHeight } from '@/mixins/workListHeight'
import WorkTitleCard from '@/components/Works/elements/WorkTitleCard.vue'
import CreatorCard from '@/components/Works/elements/CreatorCard.vue'
import { dictionaryNames, fileTypeId, workMode, workTypeName } from '@/scripts/consts'
import CardRow from '@/components/ui/row/CardRow.vue'
import { filesForWork } from '@/mixins/filesForWork'

export default {
  name: 'PublicationPage',

  metaInfo() {
    return {
      title: this && this.workTypeRusName(workTypeName.Publication)
    }
  },

  components: {
    CardRow,
    CreatorCard,
    CommentCard,
    PublicationPagesInTheEditionCard,
    PublicationCard,
    AuthorsCard,
    TitleCard,
    WorkTitleCard
  },

  mixins: [workListHeight, filesForWork],

  created() {
    this.isShowMode = this.$route.params.mode === workMode.Show
    this.isProcessMode = this.$route.params.mode === workMode.Process
    this.isEditMode = this.$route.params.mode === workMode.Edit
    this.publication = new Publication({
      commonWork: {
        isEffContract: true
      }
    })
    this.getData()
  },

  data: () => ({
    publication: new Publication(),
    dataLoaded: false,
    isShowMode: false,
    isEditMode: false,
    isProcessMode: false,
    publicationInvalid: false,
    authorsInvalid: false,
    pagesInTheEditionCardInvalid: false,
    saveBtnLoading: false,
    isNoChanges: true
  }),

  computed: {
    workTypeName() {
      return workTypeName
    },
    ...mapGetters('workTypes', ['workTypeByName', 'workTypeRusName']),

    invalid() {
      return this.authorsInvalid || this.publicationInvalid || this.pagesInTheEditionCardInvalid
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    ...mapActions('workTypes', ['getWorkTypes']),

    async getData() {
      if (!this.isShowMode)
        await this.getDictionaries([
          dictionaryNames.Year,
          dictionaryNames.PublicationForm,
          dictionaryNames.Catalog,
          dictionaryNames.Bibliography,
          dictionaryNames.WhiteListLevel
        ])
      await this.getWorkTypes()
      await this.getPublication()
      this.dataLoaded = true
      this.isNoChanges = true
    },

    async getPublication() {
      try {
        if (this.$route.params?.id !== '0') {
          const data = await publicationApi.get(this.$route.params?.id)
          await this.getFileList(data.commonWork?.id)
          this.publication = new Publication(data)
        }
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить данные по работе')
      }
    },

    async savePublication(isThrowError = false) {
      try {
        this.saveBtnLoading = true

        if (!this.publication.id) this.publication.commonWork.workType = this.workTypeByName(workTypeName.Publication)

        const body = this.publication.getForAPI()

        const data = this.publication.id ? await publicationApi.update(body) : await publicationApi.create(body)

        await this.removeFileFromAPI()

        const agreementFileListForSave = this.agreementFileList.filter(file => !file.id)

        if (agreementFileListForSave.length)
          await this.saveFile(data?.commonWork?.id, agreementFileListForSave, fileTypeId.agreement)

        if (!this.publication.id) await this.$router.replace('/works/publication/edit/' + data.id)

        await this.getFileList(data.commonWork?.id)
        this.publication = new Publication(data)
        this.$toast.info('Работа успешно сохранена')
        queueMicrotask(() => (this.isNoChanges = true))
      } catch (e) {
        console.log(e)
        if (e.message === 'Ошибка удаления файлов') this.getFileList(this.publication.commonWork?.id)
        else this.showErrorMessage(e, 'Ошибка сохранения работы')
        if (isThrowError) throw new Error('Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    initUploadAgreementFile(file) {
      this.uploadAgreementFile(file)
      this.isNoChanges = false
    },

    initRemoveAgreementFile(index) {
      this.removeAgreementFile(index)
      this.isNoChanges = false
    },

    async updateAuthorsInvalid(val) {
      this.authorsInvalid = val
      this.isNoChanges = false
    },

    async updatePublicationInvalid(val) {
      this.publicationInvalid = val
      this.isNoChanges = false
    },

    async updatePagesInTheEditionCardInvalid(val) {
      this.pagesInTheEditionCardInvalid = val
      this.isNoChanges = false
    }
  },

  watch: {
    dataLoaded() {
      this.setWorkListHeight()
    }
  },

  provide: function () {
    return {
      saveWork: this.savePublication
    }
  }
}
</script>

<style scoped></style>
